import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Truck, Play, AlertTriangle } from 'lucide-react';
import api, { formatCurrency } from '../services/api';

export default function AssetDepreciation() {
  const [tab, setTab] = useState('assets');
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [depPeriod, setDepPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', category: 'trucks', asset_tag: '', purchase_date: '',
    purchase_cost: '', salvage_value: '0', depreciation_method: 'slm', location: '', assigned_to: ''
  });

  useEffect(() => {
    api.get('/assets/categories').then(r => setCategories(r.data || [])).catch(() => {});
    loadAssets(); loadSummary();
  }, []);

  const loadAssets = () => api.get('/assets', { params: { limit: 50 } }).then(r => setAssets(r.data.assets || [])).catch(() => {});
  const loadSummary = () => api.get('/assets/summary').then(r => setSummary(r.data)).catch(() => {});
  const u = (f, v) => setForm(s => ({ ...s, [f]: v }));

  const selectedCat = categories.find(c => c.key === form.category);
  const cost = parseFloat(form.purchase_cost) || 0;
  const salvage = parseFloat(form.salvage_value) || 0;
  const rate = selectedCat ? (form.depreciation_method === 'slm' ? selectedCat.slm_rate : selectedCat.wdv_rate) : 0;
  const annualDep = form.depreciation_method === 'slm' ? Math.round((cost - salvage) * rate / 100 * 100) / 100 : Math.round(cost * rate / 100 * 100) / 100;

  const handleCreate = async () => {
    if (!form.name || !form.purchase_cost) { toast.error('Fill name and purchase cost'); return; }
    setLoading(true);
    try {
      await api.post('/assets', form);
      toast.success('Asset registered'); setShowForm(false);
      setForm({ name: '', description: '', category: 'trucks', asset_tag: '', purchase_date: '', purchase_cost: '', salvage_value: '0', depreciation_method: 'slm', location: '', assigned_to: '' });
      loadAssets(); loadSummary();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleRunDep = async () => {
    setLoading(true);
    try {
      const res = await api.post('/assets/run-depreciation', { period: depPeriod });
      toast.success(`${res.data.assets_processed} assets depreciated`); loadAssets(); loadSummary();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-5" data-testid="asset-depreciation">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading font-bold text-2xl text-slate-900">Fixed Assets & Depreciation</h1><p className="text-sm text-slate-500">Asset register, SLM/WDV depreciation, and tracking</p></div>
        <div className="flex gap-2">
          <button onClick={() => setShowForm(true)} className="btn-accent flex items-center gap-2" data-testid="add-asset-btn"><Plus className="w-4 h-4" /> Add Asset</button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border border-slate-200 rounded-md p-4"><p className="text-xs text-slate-500">Total Assets</p><p className="font-mono font-bold text-xl">{summary.totals.count}</p></div>
          <div className="bg-white border border-slate-200 rounded-md p-4"><p className="text-xs text-slate-500">Total Cost</p><p className="font-mono font-bold text-xl">Rs. {formatCurrency(summary.totals.total_cost)}</p></div>
          <div className="bg-white border border-slate-200 rounded-md p-4"><p className="text-xs text-slate-500">Accum. Depreciation</p><p className="font-mono font-bold text-xl text-red-600">Rs. {formatCurrency(summary.totals.total_depreciation)}</p></div>
          <div className="bg-white border border-slate-200 rounded-md p-4"><p className="text-xs text-slate-500">Net Book Value</p><p className="font-mono font-bold text-xl text-green-600">Rs. {formatCurrency(summary.totals.total_book_value)}</p></div>
        </div>
      )}

      <div className="flex items-center gap-2 border-b border-slate-200">
        {[['assets', 'Asset Register'], ['summary', 'Category Summary'], ['depreciation', 'Run Depreciation']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-orange-500 text-orange-600' : 'border-transparent text-slate-500'}`}>{l}</button>
        ))}
      </div>

      {showForm && (
        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4">
          <h3 className="font-heading font-semibold text-slate-800">Register New Asset</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><label className="form-label">Asset Name</label><input type="text" value={form.name} onChange={e => u('name', e.target.value)} className="form-input" data-testid="asset-name" /></div>
            <div><label className="form-label">Category</label><select value={form.category} onChange={e => u('category', e.target.value)} className="form-input" data-testid="asset-category">{categories.map(c => <option key={c.key} value={c.key}>{c.name}</option>)}</select></div>
            <div><label className="form-label">Asset Tag</label><input type="text" value={form.asset_tag} onChange={e => u('asset_tag', e.target.value)} className="form-input" placeholder="AST-001" /></div>
            <div><label className="form-label">Purchase Date</label><input type="date" value={form.purchase_date} onChange={e => u('purchase_date', e.target.value)} className="form-input" /></div>
            <div><label className="form-label">Purchase Cost</label><input type="number" value={form.purchase_cost} onChange={e => u('purchase_cost', e.target.value)} className="form-input font-mono text-right" data-testid="asset-cost" /></div>
            <div><label className="form-label">Salvage Value</label><input type="number" value={form.salvage_value} onChange={e => u('salvage_value', e.target.value)} className="form-input font-mono text-right" /></div>
            <div><label className="form-label">Method</label><select value={form.depreciation_method} onChange={e => u('depreciation_method', e.target.value)} className="form-input"><option value="slm">SLM (Straight Line)</option><option value="wdv">WDV (Written Down)</option></select></div>
            <div className="bg-orange-50 rounded-md p-3"><p className="text-xs text-slate-500">Rate: {rate}% | Annual Dep.</p><p className="font-mono font-bold text-orange-600">Rs. {formatCurrency(annualDep)}</p></div>
            <div><label className="form-label">Location</label><input type="text" value={form.location} onChange={e => u('location', e.target.value)} className="form-input" /></div>
            <div><label className="form-label">Assigned To</label><input type="text" value={form.assigned_to} onChange={e => u('assigned_to', e.target.value)} className="form-input" /></div>
            <div className="col-span-2"><label className="form-label">Description</label><input type="text" value={form.description} onChange={e => u('description', e.target.value)} className="form-input" /></div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={loading} className="btn-accent" data-testid="save-asset-btn">{loading ? 'Saving...' : 'Register Asset'}</button>
            <button onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {tab === 'assets' && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-xs uppercase text-slate-600"><th className="p-3 text-left">Asset</th><th className="p-3 text-left">Category</th><th className="p-3 text-right">Cost</th><th className="p-3 text-right">Accum. Dep.</th><th className="p-3 text-right">Book Value</th><th className="p-3 text-center">Status</th></tr></thead>
            <tbody>{assets.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-slate-400">No assets registered</td></tr> :
              assets.map(a => (
                <tr key={a.asset_id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-3"><p className="font-medium">{a.name}</p><p className="text-xs text-slate-400">{a.asset_tag || '-'} | {a.location}</p></td>
                  <td className="p-3"><span className="bg-slate-100 px-2 py-0.5 rounded text-xs">{a.category_name}</span><p className="text-xs text-slate-400 mt-0.5">{a.depreciation_method?.toUpperCase()} @ {a.depreciation_rate}%</p></td>
                  <td className="p-3 text-right font-mono">{formatCurrency(a.purchase_cost)}</td>
                  <td className="p-3 text-right font-mono text-red-600">{formatCurrency(a.accumulated_depreciation)}</td>
                  <td className="p-3 text-right font-mono font-medium text-green-600">{formatCurrency(a.book_value)}</td>
                  <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded text-xs ${a.status === 'active' ? 'bg-green-100 text-green-700' : a.status === 'fully_depreciated' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{a.status}</span></td>
                </tr>
              ))}</tbody>
          </table>
        </div>
      )}

      {tab === 'summary' && summary && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-xs uppercase text-slate-600"><th className="p-3 text-left">Category</th><th className="p-3 text-right">Count</th><th className="p-3 text-right">Total Cost</th><th className="p-3 text-right">Depreciation</th><th className="p-3 text-right">Book Value</th><th className="p-3 text-right">Active</th></tr></thead>
            <tbody>{summary.by_category?.map(c => (
              <tr key={c.category} className="border-t border-slate-100">
                <td className="p-3 font-medium">{c.category_name}</td>
                <td className="p-3 text-right">{c.count}</td>
                <td className="p-3 text-right font-mono">{formatCurrency(c.total_cost)}</td>
                <td className="p-3 text-right font-mono text-red-600">{formatCurrency(c.total_depreciation)}</td>
                <td className="p-3 text-right font-mono font-medium">{formatCurrency(c.total_book_value)}</td>
                <td className="p-3 text-right">{c.active_count}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {tab === 'depreciation' && (
        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4">
          <h3 className="font-heading font-semibold text-slate-800">Run Monthly Depreciation</h3>
          <p className="text-sm text-slate-500">Calculate and record depreciation for all active assets for a given period.</p>
          <div className="flex items-end gap-3">
            <div><label className="form-label">Period</label><input type="month" value={depPeriod} onChange={e => setDepPeriod(e.target.value)} className="form-input" data-testid="dep-period" /></div>
            <button onClick={handleRunDep} disabled={loading} className="btn-accent flex items-center gap-2" data-testid="run-dep-btn"><Play className="w-4 h-4" /> {loading ? 'Running...' : 'Run Depreciation'}</button>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800">Running depreciation will calculate monthly depreciation based on each asset's method (SLM/WDV) and update the book values. This cannot be undone.</p>
          </div>
        </div>
      )}
    </div>
  );
}
